import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SimpleLoader from '../../components/Loader/SimpleLoader';
import { getRegions, getSchools } from '../../services/catalog/catalog';
import { getApplicationRegistrationTimeline } from '../../services/schoolStatistics/schoolStatistics';
import { ExamYearContext } from '../../utils/contexts/ExamYearContext';
import { UserContext } from '../../utils/contexts/UserContext';
import useSnackbar from '../../utils/hooks/useSnackbar';
import RegistrationTimelineChart from './RegistrationTimelineChart';
import RegistrationTimelineFilters from './RegistrationTimelineFilters';
import RegistrationTimelineTotals from './RegistrationTimelineTotals';
import {
  buildChartOptions,
  buildSeries,
  findPeak,
} from './chartOptions';
import {
  GRANULARITY_WEEK,
  MODE_BY_YEAR,
  MODE_TIMELINE,
  SERIES_WARN_THRESHOLD,
} from './constants';

// A multi-select fires once per pick, so without this every click would start
// its own request.
const DEBOUNCE_MS = 400;

const isAborted = (error) =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError';

export default function RegistrationTimeline() {
  const { yearList } = useContext(ExamYearContext);
  const { user } = useContext(UserContext);
  const { addSnackbar } = useSnackbar();

  const singleSchool = user?.roles?.some((role) => role?.code === 'VP');
  const hideRegion = user?.roles?.some((role) => role?.code?.includes('RC'));

  const [mode, setMode] = useState(MODE_BY_YEAR);
  const [granularity, setGranularity] = useState(GRANULARITY_WEEK);
  const [metric, setMetric] = useState('total');
  const [split, setSplit] = useState('total');
  const [cumulative, setCumulative] = useState(false);

  const [region, setRegion] = useState(null);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const [regions, setRegions] = useState([]);
  const [schools, setSchools] = useState([]);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blockingError, setBlockingError] = useState(null);

  const cacheRef = useRef(new Map());

  useEffect(() => {
    getRegions()
      .then((res) => {
        setRegions(
          (res?.results ?? []).map((item) => ({
            label: item.name,
            value: item.uuid,
          })),
        );
      })
      .catch(() => {});

    // page_size has to clear the real number of schools: the endpoint is
    // paginated and a short page would silently hide schools from the filter.
    getSchools({ page_size: 200 })
      .then((res) => {
        setSchools(
          (res?.results ?? []).map((school) => ({
            label: `${school.school_id ?? ''} ${school.name ?? ''}`.trim(),
            value: school.uuid,
            // Plain FK on a depth-0 serializer, so this is the region uuid,
            // which is exactly what the region options carry as their value.
            region: school.region,
          })),
        );
      })
      .catch(() => {});
  }, []);

  const visibleSchools = useMemo(
    () => (region ? schools.filter((school) => school.region === region) : schools),
    [schools, region],
  );

  useEffect(() => {
    if (!region) return;
    setSelectedSchools((current) => {
      const next = current.filter((uuid) =>
        visibleSchools.some((school) => school.value === uuid),
      );
      // Same array when nothing was dropped, so this cannot loop.
      return next.length === current.length ? current : next;
    });
  }, [region, visibleSchools]);

  const yearOptions = useMemo(
    () =>
      (yearList ?? [])
        .map((item) => ({ label: String(item.year), value: item.year }))
        .sort((a, b) => b.value - a.value),
    [yearList],
  );

  const params = useMemo(() => {
    const next = { mode, granularity, metric, split };
    if (!singleSchool) {
      if (region) next.region = region;
      if (selectedSchools.length) next.school = selectedSchools.join(',');
    }
    if (mode === MODE_TIMELINE) {
      if (dateFrom) next.date_from = moment(dateFrom).format('YYYY-MM-DD');
      if (dateTo) next.date_to = moment(dateTo).format('YYYY-MM-DD');
    } else if (selectedYears.length) {
      next.years = [...selectedYears].sort((a, b) => a - b).join(',');
    }
    return next;
  }, [
    mode,
    granularity,
    metric,
    split,
    singleSchool,
    region,
    selectedSchools,
    selectedYears,
    dateFrom,
    dateTo,
  ]);

  const queryKey = useMemo(() => JSON.stringify(params), [params]);

  const load = useCallback(
    (signal) => {
      setLoading(true);
      getApplicationRegistrationTimeline({ params, signal })
        .then((res) => {
          cacheRef.current.set(queryKey, res);
          setPayload(res);
          setBlockingError(null);
        })
        .catch((error) => {
          if (isAborted(error)) return;
          const data = error?.response?.data;
          if (data?.code === 'REG_TL_TOO_MANY_SERIES' || data?.code === 'REG_TL_BAD_PARAM') {
            setPayload(null);
            setBlockingError(data.error);
            return;
          }
          addSnackbar('Не удалось загрузить статистику регистраций', 'error');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [params, queryKey, addSnackbar],
  );

  useEffect(() => {
    const cached = cacheRef.current.get(queryKey);
    if (cached) {
      setPayload(cached);
      setBlockingError(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => load(controller.signal), DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [queryKey, load]);

  const series = useMemo(
    () => buildSeries(payload, cumulative),
    [payload, cumulative],
  );
  const options = useMemo(
    () =>
      buildChartOptions({
        mode: payload?.mode ?? mode,
        granularity: payload?.granularity ?? granularity,
        cumulative,
      }),
    [payload, mode, granularity, cumulative],
  );
  const peak = useMemo(() => findPeak(payload), [payload]);

  const derivedYearWarning = useMemo(() => {
    if (!payload?.notes?.length) return null;
    const labels = {
      feb29_folded: '29 февраля сложено с 28 февраля',
      w53_folded: '53-я неделя сложена с 52-й',
    };
    return payload.notes.map((note) => labels[note] ?? note).join('; ');
  }, [payload]);

  return (
    <div className='flex flex-col gap-4'>
      <RegistrationTimelineFilters
        mode={mode}
        setMode={setMode}
        granularity={granularity}
        setGranularity={setGranularity}
        metric={metric}
        setMetric={setMetric}
        split={split}
        setSplit={setSplit}
        cumulative={cumulative}
        setCumulative={setCumulative}
        region={region}
        setRegion={setRegion}
        regions={regions}
        selectedSchools={selectedSchools}
        setSelectedSchools={setSelectedSchools}
        schools={visibleSchools}
        selectedYears={selectedYears}
        setSelectedYears={setSelectedYears}
        yearOptions={yearOptions}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        singleSchool={singleSchool}
        hideRegion={hideRegion}
      />

      <RegistrationTimelineTotals
        totals={payload?.totals}
        peak={peak}
        range={payload?.range}
      />

      {derivedYearWarning && (
        <div className='rounded-md bg-yellow-100 px-4 py-2 text-sm text-yellow-900'>
          {derivedYearWarning}
        </div>
      )}

      {series.length > SERIES_WARN_THRESHOLD && (
        <div className='rounded-md bg-yellow-100 px-4 py-2 text-sm text-yellow-900'>
          Слишком много линий ({series.length}). Выберите меньше школ или лет.
        </div>
      )}

      {blockingError && (
        <div className='rounded-md bg-red-100 px-4 py-2 text-sm text-red-900'>
          {blockingError}
        </div>
      )}

      {/* Only the chart panel swaps to the loader: unmounting the filter bar on
          every toggle would close the multi-select and drop focus. */}
      <div className='h-[28rem] w-full rounded-md bg-white p-4 shadow-sm'>
        {loading ? (
          <SimpleLoader className='h-16' />
        ) : (
          <RegistrationTimelineChart series={series} options={options} />
        )}
      </div>
    </div>
  );
}
